/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// react
import { useRef, useEffect, useState } from 'react';

// mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import {
	isNotEmpty,
	matches,
	useForm,
} from '@mantine/form';

// Styles
import classes from './UpdateUser.module.css';

// local components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';
import { UpdateUserForm } from './UpdateUserForm/UpdateUserForm';

// store
import useStore from '@store/index';

// i18n
import { useTranslation } from 'react-i18next';
import { TFormValuesUser } from '../../types';
import { EPlatformRole } from '@global-types/api/enum';
import {
	REGEX_EMAIL,
	REGEX_PHONE_NUMBER,
} from '@config/constants/regex';
import isNil from 'lodash/isNil';
import { NOT_APPLICABLE } from '@config/constants/common';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const UpdateUser = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
		usersSelectedRow,
	} = useStore();

	// Validators
	const form = useForm<TFormValuesUser>({
		initialValues: {
			role: EPlatformRole.USER,
			organization: null,
			language: 'fr',
			email: '',
			firstName: '',
			lastName: '',
			phoneNumber: '',
			avatar: null,
		},
		validate: {
			organization: (value, values) =>
				values.role === 'USER' && !value
					? t('global.organizationRequired')
					: null,
			role: isNotEmpty(t('global.roleRequired')),
			firstName: isNotEmpty(
				t('global.firstNameRequired')
			),
			lastName: isNotEmpty(t('global.lastNameRequired')),
			email: matches(
				REGEX_EMAIL,
				'form.error.invalidEmail'
			),
			phoneNumber: matches(
				REGEX_PHONE_NUMBER,
				'form.error.invalidPhoneNumber'
			),
		},
	});

	useEffect(() => {
		if (!isNil(usersSelectedRow)) {
			form.setValues({
				firstName: usersSelectedRow.firstName,
				lastName: usersSelectedRow.lastName,
				...(usersSelectedRow?.phoneNumber ===
				NOT_APPLICABLE
					? { phoneNumber: '' }
					: {
							phoneNumber: usersSelectedRow.phoneNumber,
						}),
				...(usersSelectedRow?.avatar
					? { avatar: usersSelectedRow.avatar }
					: {
							avatar: null,
						}),

				role: usersSelectedRow.role,
				organization:
					usersSelectedRow?.organization?.id || '',
				language: usersSelectedRow.language,
				email: usersSelectedRow.email,
				disabled: !usersSelectedRow.disabled,
			});

			form.resetDirty();
		}
	}, [usersSelectedRow]);

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t('users.backToAllUsersSure')}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t('users.backToAllUsers')}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<UpdateUserForm
								open={props.opened}
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
								selectedItem={usersSelectedRow}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />
					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
